import React from "react";
import {Tooltip} from "@mui/material";
import {ActionsMenuItem} from "./ActionsMenuItem";
import {ActionsMenuItemLabel} from "./ActionsMenuItemLabel";
import {
  ActionTypeIcons,
  type ActionType,
  executeDownload,
  executeOpenTab,
  ActionPayload,
  type OnActionCallback,
} from "../../../util/ActionUtils";

interface ActionItemProps<T,>{
  label: string;
  icon?: React.ReactNode;
  postIcon?: React.ReactNode;
  onClick?: () => void;
  show?: boolean;
  enable?: boolean;
  /** Shown on hover/focus, e.g. to explain why the action is disabled. */
  tooltip?: string;
  type?: ActionType;
  instrument?: T;
  actionPayload?: ActionPayload<T>;
  onAction?: OnActionCallback;
}

/**
 * Reusable action menu item component that wraps an icon and label
 * in the standard ActionsMenuItem pattern.
 */
const ActionItem = <T,>({
  label,
  icon,
  postIcon,
  onClick,
  show = true,
  enable = true,
  tooltip,
  type,
  instrument,
  actionPayload,
  onAction
}: ActionItemProps<T>) => {

  const handleClick = () => {
    if (!show || !enable) return;

    if (onClick) {
      onClick();
    } else if (type === 'DOWNLOAD' && actionPayload && instrument !== undefined) {
      executeDownload(instrument, actionPayload, onAction);
    } else if (type === 'OPEN_TAB' && actionPayload && instrument !== undefined) {
      executeOpenTab(instrument, actionPayload, onAction);
    } else {
      console.warn(`No action defined for this action item: ${label}`);
    }
  };

  if (!show) return null;

  const menuItem = (
    <ActionsMenuItem onClick={handleClick} disabled={!enable}>
      {icon}
      <ActionsMenuItemLabel>{label}</ActionsMenuItemLabel>
      {postIcon ? postIcon : type && Boolean(ActionTypeIcons[type]) && ActionTypeIcons[type]}
    </ActionsMenuItem>
  );

  if (!tooltip) return menuItem;

  // Disabled menu items ignore pointer events and focus, so the wrapper receives hover and focus for the tooltip.
  return (
    <Tooltip title={tooltip} placement="left" describeChild enterTouchDelay={0}>
      <span tabIndex={enable ? undefined : 0}>{menuItem}</span>
    </Tooltip>
  );
};

export { ActionItem };